import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The menu behind the avatar — a *person*'s menu, never a "user" menu (C11 / the glossary's
 * `user` row).
 *
 * It carries the credit balance, because the design puts it here and because it is the one
 * number a customer looks for without knowing which screen owns it.
 *
 * AMENDED 2026-10-05 (plan-014 A4, FR12-SCR-05): "My sign-in and devices" (the personal My security
 * page, the prototype's `org:me` row) is offered to the person themself — never while a WeTalk
 * operator acts for them, whose session the personal routes refuse (design §27.5.5). With the
 * organization alone open the workspace rows and the credit line are left out: they describe a
 * workspace, and there is none.
 *
 * AMENDED 2026-10-09 (design sync): "Workspaces & team" is "Workspaces", with the four-squares glyph and the
 * count beside it, and opens Organization → Workspaces; "Documentation" left the menu (it is in the sidebar).
 *
 * AMENDED 2026-10-15 (plan-019 WAPP-HOST, OD-24): a plain "Help" item while the help assistant is on — it opens the help
 * panel, or the `/help` page when a blocker hides the widget; none while an operator acts for the person.
 *
 * AMENDED 2026-10-15 (design audit): the balance is the prototype's "Credit balance" strip — a surface-coloured box
 * under the person's name, the label left and the figure right — in place of an "Available" menu row.
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
// plan-019 WAPP-HOST (OD-24): the plain "Help" item — the panel, or the `/help` page when the widget is unavailable.
import { HelpMenuItem } from "../help/help-entry.js";
import { usePopover } from "./popover.js";
import { useTheme } from "./theme.js";
import { formatMoney } from "../money.js";
import type { BillingStateView, PersonView } from "../api/gateway.js";

export interface PersonMenuProps {
  readonly person: PersonView;
  readonly billing: BillingStateView | undefined;
  /** How many workspaces the listing names (the "Workspaces" row's figure), or `null` while unknown. */
  readonly workspaceCount: number | null;
  readonly onLogOut: () => void;
  /** Whether a workspace is open: its rows (settings, team, billing) and the credit line are drawn. */
  readonly workspaceOpen: boolean;
  /** Whether "My sign-in and devices" is offered — the person's own session, not an operator's. */
  readonly personalSecurityOpen: boolean;
}

export function PersonMenu({ person, billing, workspaceCount, onLogOut, workspaceOpen, personalSecurityOpen }: PersonMenuProps): ReactNode {
  usePortalTranslation("shell");
  const { open, anchorRef, triggerRef, toggle, close } = usePopover();
  const { theme, toggle: toggleTheme } = useTheme();

  return (
    <div className="c-pop-anchor" ref={anchorRef}>
      <button
        type="button"
        ref={triggerRef}
        className="c-person-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        // At phone width only the avatar shows (review #118, fix round R); the name stays spoken.
        aria-label={person.displayName}
        onClick={toggle}
      >
        <span className="c-person-avatar">{person.initials}</span>
        <span className="c-person-name">{person.displayName.split(" ")[0]}</span>
        <span className="c-person-caret">
          <Icon glyph={GLYPH.caretDown} size="xs" tone="muted" />
        </span>
      </button>

      {open ? (
        <div className="c-pop c-pop-right" role="menu" aria-label={portalText("shell", "your_account_4ab29102")}>
          <span className="c-pop-item">
            <span className="c-mark c-mark-round c-mark-lg">{person.initials}</span>
            <span className="c-pop-item-body">
              <span className="c-pop-item-title">{person.displayName}</span>
              <span className="c-pop-item-dek">{person.emailAddress}</span>
            </span>
          </span>

          {workspaceOpen ? (
            <span className="c-person-balance">
              <span className="c-person-balance-label">{portalText("shell", "person.creditBalance")}</span>
              <span className="c-person-balance-value wt-numeric">
                {billing === undefined ? "—" : formatMoney(billing.balance)}
              </span>
            </span>
          ) : null}

          <div className="c-pop-rule" />

          {workspaceOpen ? (
            <>
              <Link to="/settings" role="menuitem" className="c-pop-item" onClick={close}>
                <Icon glyph={GLYPH.personCircle} size="lg" />
                <span className="c-pop-item-body">{portalText("shell", "account_settings_82cf8a5f")}</span>
              </Link>
              <Link to="/organization/workspaces" role="menuitem" className="c-pop-item" onClick={close}>
                <Icon glyph={GLYPH.squaresFour} size="lg" />
                <span className="c-pop-item-body">{portalText("shell", "workspaces_205b4561")}</span>
                {workspaceCount === null ? null : <span className="c-pop-item-meta">{String(workspaceCount)}</span>}
              </Link>
              <Link to="/billing" role="menuitem" className="c-pop-item" onClick={close}>
                <Icon glyph={GLYPH.receipt} size="lg" />
                <span className="c-pop-item-body">{portalText("shell", "billing_credit_e14e14af")}</span>
              </Link>
            </>
          ) : null}
          {personalSecurityOpen ? (
            <Link to="/me/security" role="menuitem" className="c-pop-item" onClick={close}>
              <Icon glyph={GLYPH.fingerprint} size="lg" />
              <span className="c-pop-item-body">{portalText("shell", "my_sign_in_and_devices_f7dc5055")}</span>
            </Link>
          ) : null}
          <HelpMenuItem onChosen={close} />
          <button type="button" role="menuitem" className="c-pop-item" onClick={toggleTheme}>
            <Icon glyph={theme === "light" ? GLYPH.moon : GLYPH.sun} size="lg" />
            <span className="c-pop-item-body">{theme === "light" ? portalText("shell", "dark_mode_9cf83d1f") : portalText("shell", "light_mode_3d3791aa")}</span>
          </button>

          <div className="c-pop-rule" />

          <button type="button" role="menuitem" className="c-pop-item" onClick={onLogOut}>
            <Icon glyph={GLYPH.signOut} size="lg" tone="critical" />
            <span className="c-pop-item-body">
              <span className="c-pop-item-title">{portalText("shell", "log_out_6e78c91f")}</span>
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
